import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";

const FAQS = [
    {
        q: "What exactly is Ago GenApp?",
        a: "Ago GenApp is a privacy-first family story app. It gives your family one secure place to store and organize photos, videos, voice recordings, written stories, and life milestones — structured as a living timeline that can be passed down across generations.",
    },
    {
        q: "Are my photos and videos stored in full original quality?",
        a: "Yes — always, and this is a promise. Ago GenApp never compresses, shrinks, or re-encodes your photos, videos, or voice recordings. The file you upload is the exact file your grandchildren will open decades from now, pixel for pixel. We believe families deserve a true archive, not a \"good enough\" copy — preserving original quality is a core part of what your storage plan pays for.",
    },
    {
        q: "How is my family’s privacy protected?",
        a: "Your archive is invite-only and encrypted. There are no ads, no data selling, and no public profiles. Only the family members you invite can ever see your memories, and you can export or delete everything at any time.",
    },
    {
        q: "What do I get as a Founding Supporter?",
        a: "Founding families are the ones who believed in us first. Depending on your tier you receive a permanent Founding badge on your profile, extended free storage (1 GB instead of 250 MB — forever), Premium access, and priority votes on what we build next.",
    },
    {
        q: "What’s the difference between one-time and monthly support?",
        a: "One-time support is a single contribution that unlocks your tier immediately. Monthly support (from $10 to $100/month, or any custom amount from $10 up) is a recurring contribution for people who want to back the project steadily — you can cancel anytime and keep your tier benefits.",
    },
    {
        q: "Is Ago GenApp live?",
        a: "Yes — the app is live and free to join today. The first 100 families who register keep their account free forever, with full access to photos, videos, voice stories, milestones, and the family tree.",
    },
    {
        q: "How can I invite other family members to join?",
        a: "From the Family page you can share your family code, create personal invite links, send them via SMS or WhatsApp, or show a QR code — inviting grandparents takes seconds. You can also create Managed Profiles for relatives who don't use apps themselves.",
    },
    {
        q: "What if my family speaks different languages?",
        a: "That's exactly who we build for. Voice stories get automatic transcripts, and when your family enables AI translation, every story told in another language gets an English translation under the transcript. The original voice recording is always preserved — transcripts and translations are an accessibility layer, never a replacement for the person's voice.",
    },
];

export default function Faq() {
    const [open, setOpen] = useState(0);
    return (
        <section id="faq" data-testid="faq-section" className="mx-auto max-w-4xl px-6 py-28 md:py-36 lg:px-10">
            <motion.div
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
            >
                <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">Chapter 04 — Questions</p>
                <h2 className="font-display text-3xl leading-tight tracking-tight md:text-5xl">
                    Everything families <span className="italic text-[#2C3347]">ask us.</span>
                </h2>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="mt-12"
            >
                {FAQS.map((item, i) => (
                    <div key={item.q} data-testid={`faq-item-${i}`} className="border-b border-warmgray">
                        <button
                            data-testid={`faq-trigger-${i}`}
                            onClick={() => setOpen(open === i ? -1 : i)}
                            aria-expanded={open === i}
                            className="flex w-full items-center justify-between gap-6 py-6 text-left"
                        >
                            <span className="font-display text-lg md:text-xl">{item.q}</span>
                            <motion.span animate={{ rotate: open === i ? 45 : 0 }} transition={{ duration: 0.3 }} className="shrink-0 text-navy">
                                <Plus size={20} />
                            </motion.span>
                        </button>
                        <AnimatePresence initial={false}>
                            {open === i && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
                                    className="overflow-hidden"
                                >
                                    <p className="max-w-2xl pb-7 text-sm leading-relaxed text-navy md:text-base">{item.a}</p>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </motion.div>
        </section>
    );
}
